import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { QUESTIONS, RACE_GOALS, buildQuestions } from "@/lib/bilanQuestions";
import { useFeatures } from "@/lib/features";
import AuthModal from "@/components/AuthModal";
import {
  ArrowRight, ArrowLeft, Check, Loader2, Sparkles, ShieldAlert, TrendingUp, Compass,
  Gauge, CalendarDays, Activity, Flag, HeartHandshake, ChevronRight,
  Share2, RotateCcw,
} from "lucide-react";

const STORE_KEY = "mai_bilan_answers";
const PROGRESS_KEY = "mai_bilan_progress";

export function encodeBilan(answers) {
  try { return encodeURIComponent(btoa(unescape(encodeURIComponent(JSON.stringify(answers))))); }
  catch { return ""; }
}
export function decodeBilan(token) {
  try { return JSON.parse(decodeURIComponent(escape(atob(decodeURIComponent(token))))); }
  catch { return null; }
}

function ProgressBar({ value }) {
  return (
    <div className="h-1.5 w-full rounded-full bg-obsidian/10 overflow-hidden">
      <motion.div className="h-full rounded-full bg-sage" initial={{ width: 0 }}
        animate={{ width: `${value}%` }} transition={{ type: "spring", stiffness: 120, damping: 20 }} />
    </div>
  );
}

function MaiBubble({ text }) {
  if (!text) return null;
  return (
    <motion.div key={text} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="mt-6 flex items-start gap-3" data-testid="mai-feedback">
      <span className="shrink-0 mt-0.5 w-8 h-8 rounded-full bg-sage/12 flex items-center justify-center">
        <Sparkles size={15} className="text-sage" />
      </span>
      <div className="rounded-2xl rounded-tl-sm bg-sage/8 border border-sage/15 px-4 py-3 text-sm text-obsidian/80 leading-relaxed">
        <span className="text-[11px] font-semibold tracking-wide text-sage uppercase block mb-0.5">MAI</span>
        {text}
      </div>
    </motion.div>
  );
}

export default function Decouverte() {
  const navigate = useNavigate();
  const features = useFeatures();
  const questions = useMemo(() => buildQuestions(features), [features]);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState("quiz"); // quiz | loading | result
  const [result, setResult] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [dir, setDir] = useState(1);
  const [resumed, setResumed] = useState(false);

  // Reprise : restaure un bilan en cours au montage.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(PROGRESS_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        if (p?.answers && Object.keys(p.answers).length) {
          setAnswers(p.answers);
          setIdx(Math.max(0, Math.min(p.idx || 0, questions.length - 1)));
          setResumed(true);
        }
      }
    } catch {}
  }, []);

  // Sauvegarde continue de la progression (pour reprendre plus tard).
  useEffect(() => {
    if (phase !== "quiz") return;
    if (!Object.keys(answers).length) return;
    localStorage.setItem(PROGRESS_KEY, JSON.stringify({ answers, idx }));
  }, [answers, idx, phase]);

  const restart = () => {
    localStorage.removeItem(PROGRESS_KEY);
    setAnswers({}); setIdx(0); setResumed(false); setResult(null); setPhase("quiz");
  };

  const visible = useMemo(() => questions.filter((q) => !q.showIf || q.showIf(answers)), [answers, questions]);
  // Cache local : purge un ancien objectif « trail » resté en mémoire quand la feature est en sommeil.
  useEffect(() => {
    if (!features.trail_enabled && answers.goal === "trail") {
      setAnswers((a) => { const n = { ...a }; delete n.goal; delete n.trail_experience; return n; });
      setIdx(0);
    }
  }, [features, answers.goal]);
  const q = visible[idx];
  const progress = Math.round(((idx + (phase === "result" ? visible.length : 0)) / visible.length) * 100);

  useEffect(() => { window.scrollTo(0, 0); }, [idx, phase]);

  const setAns = (id, val) => setAnswers((a) => ({ ...a, [id]: val }));

  const currentFeedback = useMemo(() => {
    if (!q) return "";
    const v = answers[q.id];
    if (v == null) return "";
    if (q.feedbackFn) return q.feedbackFn(v);
    if (q.feedback) return q.feedback[v] || "";
    return "";
  }, [q, answers]);

  const canNext = () => {
    if (!q) return false;
    if (q.type === "group") {
      const fields = q.fields.filter((f) => !f.showIf || f.showIf(answers));
      return fields.every((f) => f.type === "multi" || answers[f.id] != null);
    }
    if (q.type === "multi" || q.skipLabel) return true; // multi/optionnels: toujours franchissables
    return answers[q.id] != null && answers[q.id] !== "";
  };

  const missingFields = () => {
    if (!q || q.type !== "group") return [];
    return q.fields.filter((f) => (!f.showIf || f.showIf(answers)) && f.type !== "multi" && answers[f.id] == null);
  };

  const goNext = async () => {
    if (idx < visible.length - 1) { setDir(1); setIdx(idx + 1); return; }
    await submit();
  };
  const goPrev = () => { if (idx > 0) { setDir(-1); setIdx(idx - 1); } };

  const submit = async () => {
    setPhase("loading");
    const recentToVolume = { "0": "0", lt1: "0", "1": "1_2", "2": "1_2", "3": "3_4", "4plus": "5plus" };
    const payload = {
      goal: answers.goal || "remise_en_forme",
      custom_distance_km: answers.goal === "autre" && answers.custom_distance_km ? Number(answers.custom_distance_km) : null,
      race_date: answers.race_date || null,
      level: answers.level || "intermediaire",
      experience: answers.experience || null,
      condition: answers.condition || null,
      frequency: Number(answers.frequency) || 3,
      max_session_duration: Number(answers.max_session_duration) || null,
      current_volume: answers.current_volume || recentToVolume[answers.recent_sessions_per_week] || null,
      vma: answers.vma ? Number(answers.vma) : null,
      pains: Array.isArray(answers.pains) ? answers.pains : [],
      recent_sessions_per_week: answers.recent_sessions_per_week || null,
      recent_weekly_volume: answers.recent_weekly_volume || null,
      continuous_capacity_min: answers.continuous_capacity_min || null,
      recent_longest_run_min: answers.recent_longest_run_min || null,
      external_load: Array.isArray(answers.external_load) ? answers.external_load : [],
      intervals_experience: answers.intervals_experience || null,
      hills_experience: answers.hills_experience || null,
      trail_experience: answers.trail_experience || null,
      stop_recent: answers.stop_recent || null,
      resumed: answers.resumed || null,
      active_pain: answers.active_pain || null,
    };
    localStorage.setItem(STORE_KEY, JSON.stringify(payload));
    try {
      const res = await api.post("/public/bilan", payload);
      setResult(res.data);
      localStorage.removeItem(PROGRESS_KEY);
      setTimeout(() => setPhase("result"), 900); // petite respiration "MAI analyse…"
    } catch (e) {
      setPhase("quiz");
      const st = e?.response?.status;
      const msg = e?.response?.data?.message || e?.response?.data?.detail;
      toast.error(st === 403 && msg ? String(msg) : "Impossible de finaliser ton bilan pour l'instant. Tes réponses sont conservées — réessaie dans un instant.", { duration: 7000 });
    }
  };

  const share = async () => {
    const stored = (() => { try { return JSON.parse(localStorage.getItem(STORE_KEY) || "{}"); } catch { return {}; } })();
    const url = `${window.location.origin}/ma-prepa?d=${encodeBilan(stored)}`;
    const shareData = { title: "Ma prépa MAI", text: "Voici ma préparation running personnalisée, construite par MAI 👟", url };
    try {
      if (navigator.share) { await navigator.share(shareData); return; }
      await navigator.clipboard.writeText(url);
      toast.success("Lien copié — partage ta prépa MAI !");
    } catch {}
  };

  // ---- QUIZ ----
  if (phase === "quiz" && q) {
    return (
      <div className="min-h-screen bg-sand">
        <div className="max-w-xl mx-auto px-5 pt-6 pb-32">
          <div className="flex items-center gap-3 mb-8">
            <img src="/logo.png" alt="MAIRUNCOACH" className="h-6 w-auto" onClick={() => navigate("/")} role="button" />
            <span className="text-xs text-obsidian/45">Bilan gratuit · sans compte</span>
          </div>
          <ProgressBar value={Math.round((idx / visible.length) * 100)} />
          <div className="mt-2 flex items-center justify-between">
            <div className="text-xs text-obsidian/45">Étape {idx + 1} / {visible.length}</div>
            {resumed && (
              <button data-testid="quiz-restart" onClick={restart}
                className="inline-flex items-center gap-1.5 text-xs text-obsidian/45 hover:text-obsidian transition-colors">
                <RotateCcw size={12} /> Recommencer
              </button>
            )}
          </div>
          {resumed && (
            <div className="mt-3 rounded-xl bg-sage/8 border border-sage/15 px-4 py-2.5 text-sm text-obsidian/70" data-testid="resume-banner">
              On reprend là où tu t'étais arrêté 👋
            </div>
          )}

          <AnimatePresence mode="wait" custom={dir}>
            <motion.div key={q.id} custom={dir}
              initial={{ opacity: 0, x: dir * 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: dir * -24 }}
              transition={{ duration: 0.28 }}>
              <h1 className="mt-8 font-display text-3xl sm:text-4xl font-light tracking-tight leading-tight" data-testid="quiz-question">{q.title}</h1>
              {q.subtitle && <p className="mt-2.5 text-obsidian/60">{q.subtitle}</p>}

              <div className="mt-7">
                {q.type === "single" && (
                  <div className="grid gap-3">
                    {q.options.map((o) => {
                      const sel = answers[q.id] === o.value;
                      return (
                        <button key={o.value} data-testid={`opt-${q.id}-${o.value}`} onClick={() => setAns(q.id, o.value)}
                          className={`text-left rounded-2xl border px-5 py-4 transition-[border-color,background-color,transform] hover:-translate-y-0.5 ${sel ? "border-sage bg-sage/8 ring-1 ring-sage" : "border-border bg-white hover:border-obsidian/25"}`}>
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <div className="font-medium text-obsidian">{o.label}</div>
                              {o.hint && <div className="text-sm text-obsidian/50 mt-0.5">{o.hint}</div>}
                            </div>
                            <span className={`shrink-0 w-5 h-5 rounded-full border flex items-center justify-center ${sel ? "bg-sage border-sage text-white" : "border-obsidian/25"}`}>
                              {sel && <Check size={12} strokeWidth={3} />}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {q.type === "scale" && (
                  <div className="grid grid-cols-5 gap-2.5">
                    {q.options.map((o) => {
                      const sel = answers[q.id] === o.value;
                      return (
                        <button key={o.value} data-testid={`opt-${q.id}-${o.value}`} onClick={() => setAns(q.id, o.value)}
                          className={`rounded-2xl border py-5 text-xl font-medium transition-[border-color,background-color,transform] hover:-translate-y-0.5 ${sel ? "border-sage bg-sage/10 text-obsidian ring-1 ring-sage" : "border-border bg-white text-obsidian/70"}`}>
                          {o.label}
                        </button>
                      );
                    })}
                  </div>
                )}

                {q.type === "multi" && (
                  <div className="grid gap-3">
                    <div className="flex flex-wrap gap-2.5">
                      {q.options.map((o) => {
                        const arr = answers[q.id] || [];
                        const sel = arr.includes(o.value);
                        return (
                          <button key={o.value} data-testid={`opt-${q.id}-${o.value}`}
                            onClick={() => setAns(q.id, sel ? arr.filter((x) => x !== o.value) : [...arr.filter((x) => x !== "none"), o.value])}
                            className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-[border-color,background-color] ${sel ? "border-sage bg-sage/10 text-obsidian" : "border-border bg-white text-obsidian/65 hover:border-obsidian/25"}`}>
                            {o.label}
                          </button>
                        );
                      })}
                    </div>
                    <button data-testid={`opt-${q.id}-none`} onClick={() => setAns(q.id, [])}
                      className={`mt-1 text-left rounded-2xl border px-5 py-4 transition-colors ${(answers[q.id] || []).length === 0 ? "border-sage bg-sage/8" : "border-border bg-white hover:border-obsidian/25"}`}>
                      <span className="font-medium">{q.noneLabel}</span>
                    </button>
                  </div>
                )}

                {q.type === "group" && (
                  <div className="grid gap-6" data-testid={`group-${q.id}`}>
                    {q.fields.filter((f) => !f.showIf || f.showIf(answers)).map((f) => (
                      <div key={f.id} data-testid={`field-${f.id}`} data-missing={f.type !== "multi" && answers[f.id] == null ? "1" : "0"}>
                        <div className="text-sm font-medium text-obsidian/80">
                          {f.label}
                          {f.type !== "multi" && answers[f.id] == null && <span className="ml-2 text-[11px] font-normal text-amber-700/80">à compléter</span>}
                        </div>
                        <div className="mt-2.5 flex flex-wrap gap-2">
                          {f.options.map((o) => {
                            const isMulti = f.type === "multi";
                            const arr = answers[f.id] || [];
                            const sel = isMulti ? arr.includes(o.value) : answers[f.id] === o.value;
                            return (
                              <button key={o.value} data-testid={`opt-${f.id}-${o.value}`}
                                onClick={() => setAns(f.id, isMulti ? (sel ? arr.filter((x) => x !== o.value) : [...arr, o.value]) : o.value)}
                                className={`rounded-full border px-4 py-2 text-sm transition-[border-color,background-color] ${sel ? "border-sage bg-sage/10 text-obsidian ring-1 ring-sage" : "border-border bg-white text-obsidian/65 hover:border-obsidian/25"}`}>
                                {o.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {q.type === "date" && (
                  <div className="grid gap-3">
                    <input type="date" data-testid="input-race-date" value={answers.race_date || ""}
                      min={new Date().toISOString().slice(0, 10)}
                      onChange={(e) => setAns("race_date", e.target.value)}
                      className="w-full rounded-2xl border border-border bg-white px-5 py-4 text-base outline-none focus:border-sage focus:ring-2 focus:ring-sage/20" />
                    <button data-testid="skip-race-date" onClick={() => { setAns("race_date", ""); goNext(); }}
                      className="text-left rounded-2xl border border-border bg-white px-5 py-4 hover:border-obsidian/25 transition-colors">
                      <span className="font-medium text-obsidian/70">{q.skipLabel}</span>
                    </button>
                  </div>
                )}

                {q.type === "number" && (
                  <div className="grid gap-3">
                    <div className="flex items-center gap-3 rounded-2xl border border-border bg-white px-5 py-4 focus-within:border-sage focus-within:ring-2 focus-within:ring-sage/20">
                      <input type="number" step={q.step || 1} min={q.min} max={q.max} inputMode="decimal"
                        data-testid={`input-${q.id}`} placeholder={q.placeholder || ""}
                        value={answers[q.id] ?? ""}
                        onChange={(e) => setAns(q.id, e.target.value)}
                        className="flex-1 outline-none text-base bg-transparent" />
                      {q.unit && <span className="text-sm text-obsidian/45">{q.unit}</span>}
                    </div>
                  </div>
                )}

                {q.type === "vma" && (                  <div className="grid gap-3">
                    <div className="flex items-center gap-3 rounded-2xl border border-border bg-white px-5 py-4 focus-within:border-sage focus-within:ring-2 focus-within:ring-sage/20">
                      <Gauge size={18} className="text-sage" />
                      <input type="number" step="0.1" min="6" max="25" inputMode="decimal" data-testid="input-vma"
                        placeholder="Ex : 14.5" value={answers.vma || ""}
                        onChange={(e) => setAns("vma", e.target.value)}
                        className="flex-1 outline-none text-base bg-transparent" />
                      <span className="text-sm text-obsidian/45">km/h</span>
                    </div>
                    <button data-testid="skip-vma" onClick={() => { setAns("vma", ""); goNext(); }}
                      className="text-left rounded-2xl border border-border bg-white px-5 py-4 hover:border-obsidian/25 transition-colors">
                      <span className="font-medium text-obsidian/70">{q.skipLabel}</span>
                    </button>
                  </div>
                )}
              </div>

              <MaiBubble text={currentFeedback} />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* barre d'action fixe */}
        <div className="fixed bottom-0 inset-x-0 bg-sand/90 backdrop-blur-md border-t border-border/60">
          {!canNext() && missingFields().length > 0 && (
            <div data-testid="quiz-missing-hint" className="max-w-xl mx-auto px-5 pt-3 text-xs text-obsidian/60">
              Encore {missingFields().length === 1 ? "une réponse" : `${missingFields().length} réponses`} : « {missingFields()[0].label} »
            </div>
          )}
          <div className="max-w-xl mx-auto px-5 py-4 flex items-center justify-between gap-3">
            <button data-testid="quiz-back" onClick={goPrev} disabled={idx === 0}
              className="inline-flex items-center gap-1.5 text-sm text-obsidian/55 disabled:opacity-30 hover:text-obsidian transition-colors">
              <ArrowLeft size={16} /> Retour
            </button>
            <button data-testid="quiz-next" onClick={goNext} disabled={!canNext()}
              className="group inline-flex items-center gap-2.5 rounded-full bg-obsidian text-white px-7 py-3.5 text-sm font-medium hover:-translate-y-0.5 transition-transform disabled:opacity-40 disabled:translate-y-0">
              {idx === visible.length - 1 ? "Voir mon bilan" : "Continuer"}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- LOADING ----
  if (phase === "loading") {
    return (
      <div className="min-h-screen bg-sand flex flex-col items-center justify-center px-6 text-center">
        <span className="w-14 h-14 rounded-full bg-sage/12 flex items-center justify-center">
          <Loader2 size={26} className="text-sage animate-spin" />
        </span>
        <h2 className="mt-6 font-display text-2xl font-light tracking-tight">MAI analyse ton profil…</h2>
        <p className="mt-2 text-obsidian/55 max-w-sm">Construction de ta synthèse et des grandes lignes de ta préparation.</p>
      </div>
    );
  }

  // ---- RESULT ----
  const s = result?.synthesis || {};
  return (
    <div className="min-h-screen bg-sand">
      <div className="max-w-3xl mx-auto px-5 pt-8 pb-40">
        <div className="flex items-center justify-between gap-3 mb-7">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="MAIRUNCOACH" className="h-6 w-auto" role="button" onClick={() => navigate("/")} />
            <span className="text-xs text-obsidian/45">Ton bilan MAI · gratuit</span>
          </div>
          <button data-testid="result-share" onClick={share}
            className="inline-flex items-center gap-1.5 rounded-full border border-obsidian/15 px-4 py-2 text-xs font-medium text-obsidian/70 hover:bg-obsidian/[0.03] transition-colors">
            <Share2 size={14} /> Partager
          </button>
        </div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-wide text-sage uppercase mb-3">
            <Sparkles size={14} /> Analyse personnalisée
          </div>
          <h1 className="font-display text-3xl sm:text-5xl font-light tracking-tight leading-[1.05]" data-testid="result-title">
            Voici ce que MAI a compris de toi.
          </h1>
          <p className="mt-4 text-obsidian/70 leading-relaxed">{s.orientation}</p>
        </motion.div>

        {/* Synthèse — bento */}
        <div className="mt-8 grid sm:grid-cols-3 gap-3" data-testid="synthesis-grid">
          {[
            { Icon: Flag, k: "Objectif", v: s.objectif },
            { Icon: TrendingUp, k: "Niveau estimé", v: s.niveau_estime },
            { Icon: CalendarDays, k: "Disponibilité", v: s.disponibilite },
            { Icon: Activity, k: "Volume actuel", v: s.volume_actuel },
            { Icon: Compass, k: "Expérience", v: s.experience },
            { Icon: Gauge, k: "VMA", v: s.vma },
          ].map(({ Icon, k, v }) => (
            <div key={k} className="rounded-2xl bg-white border border-border p-4">
              <div className="flex items-center gap-2 text-[11px] tracking-wide uppercase text-obsidian/45 font-semibold">
                <Icon size={14} className="text-sage" /> {k}
              </div>
              <div className="mt-1.5 text-[15px] font-medium text-obsidian leading-snug">{v}</div>
            </div>
          ))}
        </div>

        {/* Vigilance + Axes */}
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <div className="rounded-2xl bg-white border border-border p-5">
            <div className="flex items-center gap-2 font-medium"><ShieldAlert size={17} className="text-sage" /> Points de vigilance</div>
            <ul className="mt-3 space-y-2.5">
              {(s.points_vigilance || []).map((p, i) => (
                <li key={i} className="text-sm text-obsidian/70 leading-relaxed flex gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-sage shrink-0" />{p}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl bg-white border border-border p-5">
            <div className="flex items-center gap-2 font-medium"><TrendingUp size={17} className="text-sage" /> Axes de progression</div>
            <ul className="mt-3 space-y-2.5">
              {(s.axes_progression || []).map((p, i) => (
                <li key={i} className="text-sm text-obsidian/70 leading-relaxed flex gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-sage shrink-0" />{p}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Ta préparation en un coup d'œil — grandes orientations, AUCUNE fausse séance.
            Le vrai programme (périodisation, séances) est construit par MAI V2 après inscription. */}
        <div className="mt-12" data-testid="program-teaser">
          <h2 className="font-display text-2xl sm:text-3xl font-light tracking-tight">Ta préparation, dans les grandes lignes</h2>
          <p className="mt-2 text-obsidian/60">
            À partir de ce bilan, MAI construira ton programme personnalisé — séance par séance — dès la création de ton compte.
            Rien n'est figé ici : voici seulement les orientations que ton bilan permet déjà d'anticiper.
          </p>

          <div className="mt-6 grid sm:grid-cols-3 gap-3">
            {[
              { Icon: CalendarDays, k: "Durée de préparation", v: s.weeks_total ? `${s.weeks_total} semaines` : "—" },
              { Icon: Activity, k: "Rythme conseillé", v: s.frequency ? `${s.frequency} séance${s.frequency > 1 ? "s" : ""} / semaine` : "—" },
              { Icon: TrendingUp, k: "Logique de progression", v: s.mode === "event" ? "Développement → spécifique → affûtage" : "Montée de charge progressive + allègements" },
            ].map(({ Icon, k, v }) => (
              <div key={k} className="rounded-2xl bg-white border border-border p-4" data-testid="teaser-item">
                <div className="flex items-center gap-2 text-[11px] tracking-wide uppercase text-obsidian/45 font-semibold">
                  <Icon size={14} className="text-sage" /> {k}
                </div>
                <div className="mt-1.5 text-[15px] font-medium text-obsidian leading-snug">{v}</div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-start gap-3 rounded-2xl bg-sage/[0.07] border border-sage/15 px-4 py-3.5">
            <Sparkles size={16} className="text-sage shrink-0 mt-0.5" />
            <p className="text-sm text-obsidian/75 leading-relaxed">
              Ton programme personnalisé est construit par MAI <span className="font-medium text-obsidian">après ton inscription</span>,
              directement à partir de ce bilan (aucune ressaisie). Ta <span className="font-medium text-obsidian">première vraie séance</span> t'attend juste après — offerte.
            </p>
          </div>
        </div>

        {/* Boucle MAI */}
        <div className="mt-14 rounded-[24px] bg-white border border-border p-6 sm:p-8">
          <div className="text-[11px] tracking-[0.18em] uppercase text-sage font-semibold">Ce qui rend MAI différent</div>
          <h3 className="mt-3 font-display text-2xl font-light tracking-tight">MAI ne génère pas un plan et t'oublie.</h3>
          <p className="mt-2 text-obsidian/65 leading-relaxed max-w-2xl">À chaque séance réalisée et chaque retour, MAI ajuste la suite de ta préparation. Plus tu avances, plus c'est personnalisé.</p>
          <div className="mt-6 flex flex-wrap items-center gap-x-2 gap-y-2 text-sm">
            {["Bilan", "Programme", "Entraînement", "Feedback", "Adaptation", "Suite"].map((step, i) => (
              <span key={step} className="inline-flex items-center gap-2">
                <span className="rounded-full bg-sage/10 text-obsidian/80 px-3 py-1.5 font-medium">{step}</span>
                {i < 5 && <ChevronRight size={15} className="text-obsidian/30" />}
              </span>
            ))}
          </div>
        </div>

        {/* Upsell humain */}
        <div className="mt-6 rounded-[24px] bg-obsidian text-white p-6 sm:p-8 overflow-hidden relative">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-sage font-semibold">
              <HeartHandshake size={14} /> Aller plus loin
            </div>
            <h3 className="mt-3 font-display text-2xl font-light tracking-tight">Envie d'un regard humain en plus ?</h3>
            <p className="mt-2 text-white/65 leading-relaxed">
              MAI t'accompagne de bout en bout — c'est un vrai coaching, complet à lui seul. Et si tu le souhaites,
              certaines offres ajoutent l'œil et les échanges d'un coach humain qui prend du recul sur tes grandes orientations.
            </p>
            <div className="mt-4 text-sm text-white/50">L'IA construit et adapte. L'humain t'accompagne en plus.</div>
          </div>
        </div>
      </div>

      {/* CTA fixe compte */}
      <div className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur-md border-t border-border">
        <div className="max-w-3xl mx-auto px-5 py-4 flex flex-col sm:flex-row items-center gap-3 sm:justify-between">
          <div className="text-sm text-obsidian/70 text-center sm:text-left">
            <span className="font-medium text-obsidian">MAI construit ta préparation personnalisée.</span> Ton bilan est déjà prêt — aucune ressaisie.
          </div>
          <button data-testid="result-create-account" onClick={() => setAuthOpen(true)}
            className="w-full sm:w-auto group inline-flex items-center justify-center gap-2.5 rounded-full bg-sage text-white px-7 py-3.5 text-sm font-medium hover:bg-sage/90 hover:-translate-y-0.5 transition-[transform,background-color]">
            Découvrir mon programme personnalisé <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </div>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} defaultMode="register"
        title="Crée ton compte pour lancer ta préparation"
        subtitle="MAI reprend ton bilan et construit ton programme personnalisé. Ta première séance t'attend."
        redirectPath="/app" onSuccess={() => navigate("/app")} />
    </div>
  );
}
